/**
 * HSE scoring orchestration (DEV-822).
 *
 * The HSE row on the Performance tab is a parallel scoring pipeline to the
 * existing Mission/Schedule/Budget signals. It runs alongside (not inside)
 * the legacy `computeMissionKpiSignal` because:
 *
 *   1. HSE data lives in `project_issues` (subtype='hse_incident'), not in
 *      `mission_metric_points`. Threading it through the legacy mission
 *      pipeline would require a second data source per call site.
 *   2. HSE scoring uses the new `incidentCountType` registered in the
 *      missionTypes plug-in registry. We dispatch through it directly here
 *      to keep the HSE feature decoupled from the legacy switch in
 *      mission.ts. (When mission.ts eventually becomes a thin shim over
 *      the registry, this file becomes a thin wrapper too.)
 *   3. HSE scores feed both the HSE row UI AND the floor-cap on the
 *      Mission summary pill. Computing them once and passing the bundle
 *      around keeps both consumers in sync.
 *
 * Spec: docs/features/Life4Earth_HSE_Feature_Spec.md § 6
 */

import {
  incidentCountType,
  type IncidentCountConfig,
} from './missionTypes/incidentCount';
import type {
  HseCategory,
  HseSeverity,
  MissionDataPoint,
  SubKpiScore,
} from './missionTypes/types';

/* ------------------------------------------------------------------------- */
/*  Public types                                                             */
/* ------------------------------------------------------------------------- */

/**
 * One HSE incident as seen by the scoring layer. Mirrors the columns added
 * to project_issues by migration 034, plus the issue id and title needed
 * for the foot preview.
 */
export interface HseIncident {
  /** ProjectIssue id (e.g., "INC-0023" or a UUID). Threaded through to UI. */
  issue_id: string;
  /** Required: when it actually happened. Drives streak math + period bucket. */
  incident_date: string;
  /** Which HSE card this affects. */
  hse_category: HseCategory;
  /** 5-level scale; drives card color via incidentCountType. */
  severity: HseSeverity;
  /** Title — shown in foot preview when this is the most-recent incident. */
  title?: string;
  /** Investigation status (open / investigating / etc.) — for drill-down. */
  status?: string;
  /** True when masked for the current viewer (set by API permission filter). */
  confidential?: boolean;
}

/**
 * One HSE sub-KPI configuration, as stored on a SubKpiInstance with
 * mission_type_key='incident_count' and mission_type_config = IncidentCountConfig.
 *
 * Bundled together with the resulting score for HSERow to render.
 */
export interface HseSubKpi {
  /** Unique sub-KPI instance id. */
  sub_kpi_guid: string;
  /** The configured display name (e.g., "Health"). */
  display_name: string;
  /** The full type config from mission_type_config. */
  config: IncidentCountConfig;
  /** Resolved score from the engine. */
  score: SubKpiScore;
}

/**
 * The HSE bundle for a project. Always contains exactly the cards that
 * have been provisioned for the project (typically 3 — Health/Safety/Environment).
 */
export interface HseBundle {
  /** Per-card scores, in display order. */
  cards: HseSubKpi[];
  /**
   * Worst color across all cards. Used by PerformanceSummary to apply the
   * floor cap to the Mission summary pill.
   *   - 'green'  → no override needed
   *   - 'yellow' → cap Mission to yellow (only when Mission is currently green)
   *   - 'red'    → cap Mission to red (regardless of Mission color)
   */
  worst_color: HseColor;
  /**
   * If `worst_color` is yellow or red, identifies which category drove it
   * (for the "Capped by X" caption on the summary pill). When two
   * categories tie, deterministic order is used: health > safety > environment.
   */
  capped_by_category: HseCategory | null;
}

export type HseColor = 'green' | 'yellow' | 'red';

/* ------------------------------------------------------------------------- */
/*  Color helpers                                                            */
/* ------------------------------------------------------------------------- */

function colorOfScore(score: SubKpiScore): HseColor {
  if (score.score_0_100 === null) return 'green';
  if (score.score_0_100 >= 100) return 'green';
  if (score.score_0_100 >= 70) return 'yellow';
  return 'red';
}

function worseColor(a: HseColor, b: HseColor): HseColor {
  if (a === 'red' || b === 'red') return 'red';
  if (a === 'yellow' || b === 'yellow') return 'yellow';
  return 'green';
}

/* ------------------------------------------------------------------------- */
/*  Convert raw incidents → MissionDataPoint[] for engine consumption        */
/* ------------------------------------------------------------------------- */

/**
 * Numeric severity ordinal for legacy compatibility (DEV-822). The engine
 * type's score function reads severity from metadata.severity primarily and
 * falls back to value-as-ordinal only for legacy data without metadata.
 *
 * Both scales map to 1-5 with the same intent: 1 = near-miss, 5 = worst.
 */
const SEVERITY_TO_VALUE: Record<HseSeverity, number> = {
  // OSHA (Health, Safety)
  'near-miss': 1,
  'first-aid': 2,
  'recordable': 3,
  'lost-time': 4,
  'fatality': 5,
  // EPA (Environment) — same ordinals so legacy fallback yields a sane band
  'minor': 2,
  'reportable': 3,
  'major': 4,
  'catastrophic': 5,
};

/* ------------------------------------------------------------------------- */
/*  Per-category severity helpers (DEV-822 Option B)                         */
/* ------------------------------------------------------------------------- */

/**
 * Returns the valid severities for a given HSE category, in display order
 * (lowest → highest severity). Used by the modal severity-chip picker to
 * render the right options when the category changes.
 */
export function severitiesForCategory(category: HseCategory): HseSeverity[] {
  if (category === 'environment') {
    return ['near-miss', 'minor', 'reportable', 'major', 'catastrophic'];
  }
  // Health and Safety both use the OSHA scale.
  return ['near-miss', 'first-aid', 'recordable', 'lost-time', 'fatality'];
}

/**
 * Yellow-band severities by category. The boundary is at the 3rd ordinal:
 * the first two severities (near-miss + first-aid|minor) are yellow; the
 * remaining three are red.
 */
export function severityIsYellow(severity: HseSeverity, category: HseCategory): boolean {
  if (category === 'environment') {
    return severity === 'near-miss' || severity === 'minor';
  }
  return severity === 'near-miss' || severity === 'first-aid';
}

/** Red-band severities by category — the 3rd, 4th, 5th severity in either scale. */
export function severityIsRed(severity: HseSeverity, category: HseCategory): boolean {
  if (category === 'environment') {
    return severity === 'reportable' || severity === 'major' || severity === 'catastrophic';
  }
  return severity === 'recordable' || severity === 'lost-time' || severity === 'fatality';
}

/** True when the (severity, category) pairing is valid. App-layer guard. */
export function isValidSeverityForCategory(
  severity: HseSeverity,
  category: HseCategory,
): boolean {
  return severitiesForCategory(category).includes(severity);
}

/**
 * Convert one HseIncident into a MissionDataPoint the engine type can score.
 * Severity rides through `metadata.severity` (authoritative); the value
 * field carries the ordinal as a fallback for any code path that only
 * looks at `value`.
 */
export function incidentToDataPoint(incident: HseIncident): MissionDataPoint {
  return {
    date: incident.incident_date,
    value: SEVERITY_TO_VALUE[incident.severity],
    metadata: {
      severity: incident.severity,
      title: incident.title,
      issue_id: incident.issue_id,
      status: incident.status,
      confidential: incident.confidential,
    },
  };
}

/* ------------------------------------------------------------------------- */
/*  Public API: compute the HSE bundle for a project                         */
/* ------------------------------------------------------------------------- */

/**
 * Score every HSE card for a project. Pure function — pass in the configs
 * (typically 3, one per category) and the project's full incident list,
 * get back a ranked bundle.
 *
 * The function filters incidents to each card's `hse_category` before
 * scoring, so the same incident list can be passed to every card.
 *
 * @param configs   One entry per HSE sub-KPI on the project.
 * @param incidents All HSE incidents for the project (any category).
 * @param asOfDate  Evaluation date (YYYY-MM-DD).
 */
export function computeHseBundle(
  configs: Array<{
    sub_kpi_guid: string;
    config: IncidentCountConfig;
  }>,
  incidents: HseIncident[],
  asOfDate: string,
): HseBundle {
  const cards: HseSubKpi[] = configs.map((entry) => {
    const cfg = entry.config;
    const matching = incidents.filter((i) => i.hse_category === cfg.hse_category);
    const points = matching.map(incidentToDataPoint);
    const score = incidentCountType.score(cfg, points, asOfDate);
    return {
      sub_kpi_guid: entry.sub_kpi_guid,
      display_name: cfg.display_name,
      config: cfg,
      score,
    };
  });

  // Worst color across all cards (deterministic tie-break: H > S > E).
  let worst: HseColor = 'green';
  let cappedBy: HseCategory | null = null;
  const order: HseCategory[] = ['health', 'safety', 'environment'];
  for (const cat of order) {
    const card = cards.find((c) => c.config.hse_category === cat);
    if (!card) continue;
    const c = colorOfScore(card.score);
    const next = worseColor(worst, c);
    if (next !== worst) {
      worst = next;
      cappedBy = cat;
    }
  }

  return {
    cards,
    worst_color: worst,
    // Only attribute when the floor cap actually changes anything.
    capped_by_category: worst === 'green' ? null : cappedBy,
  };
}

/* ------------------------------------------------------------------------- */
/*  Floor-cap helper for the Mission summary pill                            */
/* ------------------------------------------------------------------------- */

/**
 * Apply the HSE floor cap to a Mission color. The Mission SCORE NUMBER is
 * NOT changed — only the color is overridden.
 *
 * Asymmetric on purpose: a single recordable incident shouldn't drop the
 * Mission score from 84 to 82, but it should color-cap the summary so
 * stakeholders see honest signal.
 */
export function applyHseFloorCap(
  missionColor: HseColor,
  hse: HseBundle,
): HseColor {
  return worseColor(missionColor, hse.worst_color);
}
